import type { ReactNode } from 'react'
import { LaneCount } from 'app/components/lists/board/LaneCount'
import { LaneLoadMore } from 'app/components/lists/board/LaneLoadMore'
import type { ListBoardLaneDef } from 'app/components/lists/board/boardTypes'
import { cn } from 'app/lib/utils'
import type { ContactJson } from 'app/types/entities'
import type { ListBoardState } from 'app/types/list-board'
import type { LaneState } from 'lib/manualListLanes'

const EMPTY_LANE: LaneState<ContactJson> = { items: [], total: 0, hasMore: false, page: 0, pendingIds: [] }

/** One list stage drawn as a board lane over `useListBoard`'s state. */
export function stageLane(
  stage: { id: string; name: string; color?: string | null },
  board: Pick<ListBoardState, `lanes` | `loading` | `failedLanes` | `loadingLanes` | `loadMore`>,
  extras: { headerAction?: ReactNode; headerExtra?: ReactNode } = {},
): ListBoardLaneDef<ContactJson, string> {
  const rawLane = board.lanes[stage.id]
  const lane = rawLane ?? EMPTY_LANE
  return {
    key: stage.id,
    label: stage.name,
    labelAdornment: (
      <span className={cn(`h-2 w-2 shrink-0 rounded-full`, stage.color ? `bg-${stage.color}` : `bg-surface-strong`)} />
    ),
    count: <LaneCount shown={lane.items.length} total={lane.total} />,
    headerExtra: extras.headerExtra,
    headerAction: extras.headerAction,
    items: lane.items,
    // A lane whose own read failed says so, instead of "Loading" forever: no entry ever lands
    // in `board.lanes` for it, since a failed read never reaches the lanes.
    emptyLabel: board.failedLanes.has(stage.id)
      ? `Couldn't load this stage`
      : !rawLane || board.loading
        ? `Loading`
        : `Nothing here`,
    footer: lane.hasMore && (
      <LaneLoadMore
        remaining={lane.total - lane.items.length}
        loading={board.loadingLanes.has(stage.id)}
        onClick={() => board.loadMore(stage.id)}
      />
    ),
  }
}
